import type {
	ICreateElectionState,
	IElectionPollingUnits,
	IElectionStates,
	IGetElectionByID,
	IUpdateElectionState,
} from '../../../interface/state/IElectionState';
import type { IIRevElectionResponse } from '../../../interface/state/IRev';
import { electionType } from '../../types';
import { createAPI, deleteByIdAPI, getAPI, getByIdAPI, getFetchAPI, type IResponse, updateAPI } from '../api-utils';
import * as utils from '../utils';

interface ICreateElectionPollingUnit {
	id: number;
	name: string;
	code: string;
	ward: {
		id: number;
		code: string;
		name: string;
	};
	localGovernment: {
		id: number;
		name: string;
		code: string;
	};
	state: {
		id: number;
		name: string;
	};
}

export interface ICreateElection {
	name: string;
	electionCycleId: number;
	constituencyId: number;
	description: string;
	date: string;
	isForAllPollingUnits?: boolean;
	poolingUnits?: ICreateElectionPollingUnit[];
	wardCount?: number;
	irevReference: string;
	localGovernmentCount?: number;
}
export interface IUpdateElection {
	id: number;
	name: string;
	electionCycleId: number;
	constituencyId: number;
	description: string;
	wardCount?: number;
	isForAllPollingUnits?: boolean;
	localGovernmentCount?: number;
	electionData?: {
		poolingUnits?: ICreateElectionPollingUnit[];
	};
}

export const create = createAPI<ICreateElectionState, ICreateElection[]>({
	actionType: electionType.create,
	url: '/api/v1/Election',
});

export const getByID = getByIdAPI<IGetElectionByID>({
	actionType: electionType.getByID,
	url: '/api/v1/Election',
});

export const get = getAPI<IElectionStates>({
	actionType: electionType.get,
	url: '/api/v1/Election',
	type: 'core',
});

export const getCustom = getFetchAPI({
	actionType: electionType.getCustom,
	url: '/api/v1/Election/ReturnCustumElectionData',
});

export const getByName = getFetchAPI<undefined, { electionNames: string[] }>({
	actionType: electionType.getByName,
	url: '/api/v1/Election/getElectionsByName',
});

export const update = updateAPI<IUpdateElectionState, IUpdateElection>({
	actionType: electionType.update,
	url: '/api/v1/Election/update',
});

export const deleteById = deleteByIdAPI({
	actionType: electionType.deleteById,
	url: '/api/v1/Election/delete',
});

export const getPollingUnits = getAPI<IElectionPollingUnits>({
	actionType: electionType.getPollingUnits,
	url: '/api/v1/Election/get-polling-units',
	type: 'command',
});

export interface IIRevElection {
	election_type: string;
	state_id: string;
}

// The endpoint binds a JSON array. getFetchAPI spreads `data` into an object
// ({"0":{...}}), which the backend rejects with 400 — so post the array as-is.
export const getIREV = ({ data, onFailure, onSuccess }: IResponse<IIRevElectionResponse> & { data: IIRevElection[] }) =>
	utils.httpPostMethod({
		apiData: {
			url: '/api/v1/Irev/Election',
			header: utils.header(),
			data,
		},
		actionType: electionType.getIREV,
		onFailure,
		onSuccess,
	});

export interface IUpdateIREVReference {
	electionId: number;
	irevReference: string;
}

export const updateIREVReference = updateAPI<undefined, IUpdateIREVReference[]>({
	actionType: electionType.updateIREVReference,
	url: '/api/v1/Election/update/irev-reference',
});
